import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Stack, Button, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, Alert, Chip, Tooltip,
    Checkbox, FormControlLabel, Grid, Divider, Accordion, AccordionSummary, AccordionDetails,
    useTheme, useMediaQuery
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { getApiUrl, API_URL } from '../../config/api';

const UserManagement = () => {
    const [users, setUsers] = useState<any[]>([]);
    const [permissions, setPermissions] = useState<any[]>([]);
    const [open, setOpen] = useState(false);
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        role: 'TEACHER',
        password: '',
        permissionIds: [] as string[]
    });
    const [newPassword, setNewPassword] = useState('');
    const [status, setStatus] = useState<any>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const roles = [
        'SUPER_ADMIN', 'SCHOOL_ADMIN', 'PRINCIPAL', 'BURSAR',
        'ACCOUNTANT', 'TEACHER', 'RECEPTIONIST', 'PARENT'
    ];

    const fetchUsers = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('users'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(res.data);
        } catch (error) {
            console.error('Fetch users error:', error);
        }
    };

    const fetchAllPermissions = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            // Use the base API_URL since /permissions is its own route group
            const res = await axios.get(`${API_URL}/permissions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPermissions(res.data);
        } catch (error) {
            console.error('Fetch permissions error:', error);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchAllPermissions();
    }, []);

    const handleOpen = (user: any = null) => {
        if (user) {
            setSelectedUser(user);
            setFormData({
                email: user.email,
                name: user.name,
                role: user.role,
                password: '',
                permissionIds: user.permissions?.map((p: any) => p.permissionId) || []
            });
        } else {
            setSelectedUser(null);
            setFormData({
                email: '',
                name: '',
                role: 'TEACHER',
                password: '',
                permissionIds: []
            });
        }
        setOpen(true);
    };

    const handlePermissionToggle = (pId: string) => {
        setFormData(prev => ({
            ...prev,
            permissionIds: prev.permissionIds.includes(pId)
                ? prev.permissionIds.filter(id => id !== pId)
                : [...prev.permissionIds, pId]
        }));
    };

    const handleClearPermissions = () => {
        setFormData(prev => ({ ...prev, permissionIds: [] }));
    };

    const handleSave = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            if (selectedUser) {
                await axios.put(getApiUrl(`users/${selectedUser.id}`), formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStatus({ type: 'success', message: 'User updated successfully' });
            } else {
                await axios.post(getApiUrl('users'), formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setStatus({ type: 'success', message: 'User created successfully' });
            }
            setOpen(false);
            // Explicitly reset everything after save to prevent any state leakage
            setFormData({
                email: '',
                name: '',
                role: 'TEACHER',
                password: '',
                permissionIds: []
            });
            setSelectedUser(null);
            fetchUsers();
        } catch (error: any) {
            setStatus({ type: 'error', message: error.response?.data?.message || 'Operation failed' });
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.delete(getApiUrl(`users/${id}`), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStatus({ type: 'success', message: 'User deleted successfully' });
            fetchUsers();
        } catch (error: any) {
            setStatus({ type: 'error', message: error.response?.data?.message || 'Delete failed' });
        }
    };

    const handleResetPassword = async () => {
        if (!newPassword) return;
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.put(getApiUrl(`users/${selectedUser.id}/reset-password`),
                { password: newPassword },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setStatus({ type: 'success', message: 'Password reset successfully' });
            setResetDialogOpen(false);
            setNewPassword('');
        } catch (error: any) {
            setStatus({ type: 'error', message: error.response?.data?.message || 'Reset failed' });
        }
    };

    // Group permissions by module
    const modules = Array.from(new Set(permissions.map(p => p.module)));

    return (
        <Box>
            <Stack direction={isSmallMobile ? "column" : "row"} justifyContent="space-between" alignItems={isSmallMobile ? "flex-start" : "center"} mb={3} spacing={2}>
                <Typography variant="h6" fontWeight={700}>System Users</Typography>
                <Button
                    variant="contained"
                    fullWidth={isSmallMobile}
                    startIcon={<PersonAddIcon />}
                    onClick={() => handleOpen()}
                    sx={{ bgcolor: '#263B7C', borderRadius: '8px' }}
                >
                    ADD USER
                </Button>
            </Stack>

            {status && <Alert severity={status.type} sx={{ mb: 3 }} onClose={() => setStatus(null)}>{status.message}</Alert>}

            <TableContainer sx={{ border: '1px solid #edf2f7', borderRadius: '12px', overflowX: 'auto' }}>
                <Table sx={{ minWidth: isMobile ? 600 : 'auto' }}>
                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            {!isSmallMobile && <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>}
                            <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => {
                            const customPermissionCount = user.permissions?.length || 0;
                            return (
                                <TableRow key={user.id}>
                                    <TableCell>
                                        <Box>
                                            <Typography variant="body2">{user.name}</Typography>
                                            {customPermissionCount > 0 && (
                                                <Typography variant="caption" color="primary" fontWeight={600}>
                                                    +{customPermissionCount} Custom Permissions
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    {!isSmallMobile && <TableCell>{user.email}</TableCell>}
                                    <TableCell>
                                        <Chip
                                            label={user.role}
                                            size="small"
                                            sx={{
                                                bgcolor: user.role === 'SUPER_ADMIN' ? '#1a237e' : '#e3f2fd',
                                                color: user.role === 'SUPER_ADMIN' ? 'white' : '#1a237e',
                                                fontWeight: 600
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" justifyContent="flex-end">
                                            <Tooltip title="Reset Password">
                                                <IconButton size="small" onClick={() => { setSelectedUser(user); setResetDialogOpen(true); }}>
                                                    <VpnKeyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <IconButton size="small" color="primary" onClick={() => handleOpen(user)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(user.id)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" fullScreen={isSmallMobile}>
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white' }}>{selectedUser ? 'Edit User' : 'Create User'}</DialogTitle>
                <DialogContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <TextField
                            fullWidth label="Full Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                        <TextField
                            fullWidth label="Email Address"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={formData.role}
                                label="Role"
                                onChange={(e) => {
                                    const newRole = e.target.value;
                                    // If changing role, ask or automatically reset permission overrides
                                    // to avoid applying one role's overrides to another.
                                    setFormData({
                                        ...formData,
                                        role: newRole,
                                        permissionIds: [] // Auto-reset overrides on role change
                                    });
                                }}
                            >
                                {roles.map(role => <MenuItem key={role} value={role}>{role}</MenuItem>)}
                            </Select>
                        </FormControl>
                        {!selectedUser && (
                            <TextField
                                fullWidth label="Initial Password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                helperText="Default: School123@"
                            />
                        )}

                        <Divider sx={{ my: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="overline" color="text.secondary" fontWeight={700}>Additional Permissions (Per-User)</Typography>
                                <Button size="small" variant="text" onClick={handleClearPermissions} sx={{ textTransform: 'none', fontSize: '0.65rem' }}>
                                    Clear All
                                </Button>
                            </Stack>
                        </Divider>

                        <Box>
                            {modules.map(module => (
                                <Accordion key={module} elevation={0} sx={{ border: '1px solid #edf2f7', '&:before': { display: 'none' }, mb: 1, borderRadius: '8px !important' }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle2" fontWeight={700}>{module}</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={1}>
                                            {permissions.filter(p => p.module === module).map(p => (
                                                <Grid size={12} key={p.id}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={formData.permissionIds.includes(p.id)}
                                                                onChange={() => handlePermissionToggle(p.id)}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography variant="caption" fontWeight={600} display="block">{p.name}</Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{p.slug}</Typography>
                                                            </Box>
                                                        }
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#263B7C' }}>
                        {selectedUser ? 'UPDATE' : 'CREATE'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Password Dialog */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} fullWidth maxWidth="xs" fullScreen={isSmallMobile}>
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white' }}>Reset Password</DialogTitle>
                <DialogContent sx={{ p: isSmallMobile ? 2 : 3 }}>
                    <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>Resetting password for: <strong>{selectedUser?.name}</strong></Typography>
                    <TextField
                        fullWidth label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleResetPassword} variant="contained" sx={{ bgcolor: '#263B7C' }}>RESET PASSWORD</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserManagement;
